import { createContext, useContext } from 'react'
import { useWorkOrders } from '../hooks/useWorkOrders'
import { workOrderService } from '../services/workOrderService'
import type {
  DriverAssignmentResponse,
  WorkOrder,
  WorkOrderInvite,
  WorkOrderStatus,
} from '../types/workorder'

interface WorkOrderContextType {
  workOrders: WorkOrder[]
  isLoading: boolean
  error: string | null
  createWorkOrder: (data: Omit<WorkOrder, 'id'>) => Promise<WorkOrder>
  updateWorkOrder: (id: string, data: Partial<WorkOrder>) => Promise<WorkOrder>
  updateWorkOrderStatus: (
    workOrderId: string | number,
    status: WorkOrderStatus,
    declineReason?: string,
  ) => Promise<WorkOrder>
  assignDrivers: (
    workOrderId: string | number,
    driverIds: number[],
  ) => Promise<DriverAssignmentResponse>
  acceptWorkOrderInvite: (
    workOrderId: string | number,
    inviteId: string,
  ) => Promise<{ success: boolean }>
  getUserPendingWorkOrderInvitations: () => Promise<WorkOrderInvite[]>
  refreshWorkOrders: () => Promise<void>
}

const WorkOrderContext = createContext<WorkOrderContextType | undefined>(
  undefined,
)

export const WorkOrderProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const workOrdersData = useWorkOrders()

  const createWorkOrder = async (
    workOrderData: Omit<WorkOrder, 'id'>,
  ): Promise<WorkOrder> => {
    const response = await workOrderService.createWorkOrder(workOrderData)
    await workOrdersData.refreshWorkOrders()
    return response
  }

  const updateWorkOrder = async (
    workOrderId: string,
    workOrderData: Partial<WorkOrder>,
  ): Promise<WorkOrder> => {
    const response = await workOrderService.updateWorkOrder(
      workOrderId,
      workOrderData,
    )
    await workOrdersData.refreshWorkOrders()
    return response
  }

  const updateWorkOrderStatus = async (
    workOrderId: string | number,
    status: WorkOrderStatus,
    declineReason?: string,
  ): Promise<WorkOrder> => {
    const response = await workOrderService.updateWorkOrderStatus(
      workOrderId,
      status,
      declineReason,
    )
    await workOrdersData.refreshWorkOrders()
    return response
  }

  const assignDrivers = async (
    workOrderId: string | number,
    driverIds: number[],
  ): Promise<DriverAssignmentResponse> => {
    return await workOrderService.assignDrivers(workOrderId, driverIds)
  }

  const acceptWorkOrderInvite = async (
    workOrderId: string | number,
    inviteId: string,
  ): Promise<{ success: boolean }> => {
    const response = await workOrderService.acceptWorkOrderInvite(
      workOrderId,
      inviteId,
    )
    await workOrdersData.refreshWorkOrders()
    return response
  }

  const getUserPendingWorkOrderInvitations = async (): Promise<
    WorkOrderInvite[]
  > => {
    return await workOrderService.getUserPendingWorkOrderInvitations()
  }

  return (
    <WorkOrderContext.Provider
      value={{
        ...workOrdersData,
        createWorkOrder,
        updateWorkOrder,
        updateWorkOrderStatus,
        assignDrivers,
        acceptWorkOrderInvite,
        getUserPendingWorkOrderInvitations,
      }}
    >
      {children}
    </WorkOrderContext.Provider>
  )
}

export const useWorkOrderContext = () => {
  const context = useContext(WorkOrderContext)
  if (!context) {
    throw new Error(
      'useWorkOrderContext must be used within a WorkOrderProvider',
    )
  }
  return context
}
